/**
 * Settings Client Module
 *
 * Handles communication with the beautifi Portal Settings API for configuration management.
 * Features: portal settings query, merge with data attributes, sessionStorage caching.
 *
 * Priority Order (highest to lowest):
 * 1. Data attributes (developer control)
 * 2. Portal settings (user control via portal)
 * 3. Global defaults (fallback)
 */

import {
    AnimationConfig,
    AnimationType,
    AnimationIntensity,
    AnimationMode,
} from './types';

/**
 * Portal settings response structure
 */
export interface PortalSettingsResponse {
    /** Operation mode: 'opt-in' (explicit enable) or 'opt-out' (explicit disable) */
    operationMode: 'opt-in' | 'opt-out';
    /** Global default settings for all images */
    globalDefaults: {
        mode: AnimationMode;
        type: AnimationType;
        intensity: AnimationIntensity;
        loop: boolean;
    };
    /** Per-image settings keyed by image URL */
    images: {
        [imageUrl: string]: PortalImageSettings;
    };
}

/**
 * Per-image settings from portal
 */
export interface PortalImageSettings {
    /** Whether animation is enabled for this image */
    enabled: boolean;
    /** Animation mode (optional, uses global default if not set) */
    mode?: AnimationMode;
    /** Animation type (optional, uses global default if not set) */
    type?: AnimationType;
    /** Animation intensity (optional, uses global default if not set) */
    intensity?: AnimationIntensity;
    /** Whether to loop (optional, uses global default if not set) */
    loop?: boolean;
    /** Delay before animation starts in ms (optional) */
    delay?: number;
    /** Existing server-side Veo operation to resume (optional) */
    videoOperationId?: string;
    /** Current state of the server-side Veo job (optional) */
    videoStatus?: 'pending' | 'processing' | 'completed' | 'error' | 'retry';
    /** Ready-to-use generated video URL (optional) */
    videoUrl?: string;
}

/**
 * Configuration for the Settings Client
 */
export interface SettingsClientConfig {
    /** API key for authenticating with portal */
    apiKey: string;
    /** Settings API endpoint URL (default: production endpoint) */
    endpoint?: string;
    /** Request timeout in milliseconds (default: 5000) */
    timeout?: number;
    /** Enable response caching in sessionStorage (default: true) */
    enableCache?: boolean;
    /** Cache TTL in milliseconds (default: 5 minutes) */
    cacheTTL?: number;
    /** Maximum number of images to query per request (default: 100) */
    maxImagesPerQuery?: number;
    /** Enable debug logging (default: false) */
    debug?: boolean;
}

/**
 * Default configuration values
 */
const DEFAULT_CONFIG: Required<Omit<SettingsClientConfig, 'apiKey'>> = {
    endpoint: 'https://settings-api-oy7ayfiglq-uc.a.run.app/query',
    timeout: 5000,
    enableCache: true,
    cacheTTL: 5 * 60 * 1000, // 5 minutes
    maxImagesPerQuery: 100,
    debug: false,
};

/**
 * Cache key prefix for sessionStorage
 */
const CACHE_KEY_PREFIX = 'beautifi_settings_';

/**
 * Cached response structure
 */
interface CachedSettings {
    data: PortalSettingsResponse;
    timestamp: number;
}

/**
 * SettingsClient class
 *
 * Handles fetching animation settings from the Portal Settings API
 * with caching, merge logic, and graceful fallback.
 */
export class SettingsClient {
    private config: Required<SettingsClientConfig>;

    constructor(config: SettingsClientConfig) {
        this.config = {
            ...DEFAULT_CONFIG,
            ...config,
        } as Required<SettingsClientConfig>;
    }

    /**
     * Fetch settings for multiple images from the portal API
     *
     * @param imageUrls - Array of image URLs to fetch settings for
     * @returns Promise resolving to portal settings response, or null on error
     */
    async fetchSettings(imageUrls: string[]): Promise<PortalSettingsResponse | null> {
        // Check for cache clear parameter
        if (this.shouldClearCache()) {
            this.clearCache();
            if (this.config.debug) {
                console.log('[SettingsClient] Cache cleared via ?bf_clear_cache=1');
            }
        }

        // Handle empty array
        if (imageUrls.length === 0) {
            return null;
        }

        // Limit to maxImagesPerQuery
        let queryUrls = imageUrls;
        if (imageUrls.length > this.config.maxImagesPerQuery) {
            if (this.config.debug) {
                console.warn(
                    `[SettingsClient] Page has ${imageUrls.length} images. ` +
                    `Only first ${this.config.maxImagesPerQuery} will query portal settings. ` +
                    `Consider optimizing image count or using data attributes for additional images.`
                );
            }
            queryUrls = imageUrls.slice(0, this.config.maxImagesPerQuery);
        }

        // Check cache first
        if (this.config.enableCache) {
            const cached = this.getCachedSettings(queryUrls);
            if (cached) {
                if (this.config.debug) {
                    console.log('[SettingsClient] Cache hit for', queryUrls.length, 'images');
                }
                return cached;
            }
        }

        // Fetch settings from API
        try {
            const response = await this.fetchWithTimeout(queryUrls);
            if (response && this.config.enableCache) {
                this.cacheSettings(queryUrls, response);
            }
            return response;

        } catch (error) {
            // Graceful fallback on error
            if (this.config.debug) {
                console.warn('[SettingsClient] API error, falling back to data attributes:', error);
            }
            return null;
        }
    }

    /**
     * Merge portal settings with data attributes according to priority order
     *
     * Priority: data attributes > portal settings > global defaults
     *
     * @param imageUrl - The URL of the image
     * @param portalSettings - Settings fetched from portal API (may be null)
     * @param dataAttributes - Parsed data attributes from DOM element
     * @returns Merged animation configuration
     */
    mergeSettings(
        imageUrl: string,
        portalSettings: PortalSettingsResponse | null,
        dataAttributes: Partial<AnimationConfig>
    ): AnimationConfig {
        // Start with defaults
        const defaults: AnimationConfig = {
            enabled: false, // Safe default - no animation unless explicitly enabled
            intensity: 'subtle',
            type: 'auto',
            loop: true,
            delay: 0,
        };

        // If no portal settings, use data attributes + defaults
        if (!portalSettings) {
            return {
                ...defaults,
                ...dataAttributes,
            };
        }

        // Get portal settings for this specific image
        const imageSettings = portalSettings.images[imageUrl];
        const globalDefaults = portalSettings.globalDefaults;

        // Determine if image should be enabled based on operation mode
        let portalEnabled = false;
        if (portalSettings.operationMode === 'opt-in') {
            // Opt-in: Only enabled if explicitly set to true in portal
            portalEnabled = imageSettings?.enabled === true;
        } else {
            // Opt-out: Enabled by default unless explicitly set to false
            portalEnabled = imageSettings?.enabled !== false;
        }

        // Merge with priority: data attributes > portal image settings > global defaults > defaults
        return {
            // Enabled: In opt-in mode, portal is authoritative (portal controls what's active).
            // In opt-out mode, data attributes can override.
            enabled: portalSettings.operationMode === 'opt-in'
                ? portalEnabled
                : (dataAttributes.enabled ?? portalEnabled),

            // Mode: data attribute > portal image setting > global default
            mode:
                dataAttributes.mode ??
                imageSettings?.mode ??
                globalDefaults.mode ??
                defaults.mode,

            // Intensity: data attribute > portal image setting > global default > 'subtle'
            intensity:
                dataAttributes.intensity ??
                imageSettings?.intensity ??
                globalDefaults.intensity ??
                defaults.intensity,

            // Type: data attribute > portal image setting > global default > 'auto'
            type:
                dataAttributes.type ??
                imageSettings?.type ??
                globalDefaults.type ??
                defaults.type,

            // Loop: data attribute > portal image setting > global default > true
            loop:
                dataAttributes.loop ??
                imageSettings?.loop ??
                globalDefaults.loop ??
                defaults.loop,

            // Delay: data attribute > portal image setting > 0
            delay: dataAttributes.delay ?? imageSettings?.delay ?? defaults.delay,

            // Server-side video metadata comes only from the portal.
            videoOperationId: imageSettings?.videoOperationId,
            videoStatus: imageSettings?.videoStatus,
            videoUrl: imageSettings?.videoUrl,
        };
    }

    /**
     * Check if cache should be cleared based on URL parameter
     */
    private shouldClearCache(): boolean {
        if (typeof window === 'undefined' || !window.location) {
            return false;
        }
        const params = new URLSearchParams(window.location.search);
        return params.get('bf_clear_cache') === '1';
    }

    /**
     * Clear all cached settings from sessionStorage
     */
    clearCache(): void {
        if (typeof sessionStorage === 'undefined') {
            return;
        }

        const keysToRemove: string[] = [];
        for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith(CACHE_KEY_PREFIX)) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach((key) => sessionStorage.removeItem(key));

        if (this.config.debug && keysToRemove.length > 0) {
            console.log('[SettingsClient] Cleared', keysToRemove.length, 'cached entries');
        }
    }

    /**
     * Get cached settings if valid
     */
    private getCachedSettings(imageUrls: string[]): PortalSettingsResponse | null {
        if (typeof sessionStorage === 'undefined') {
            return null;
        }

        try {
            const cacheKey = this.getCacheKey(imageUrls);
            const cached = sessionStorage.getItem(cacheKey);

            if (!cached) {
                return null;
            }

            const parsed: CachedSettings = JSON.parse(cached);

            // Check if cache is still valid
            if (Date.now() - parsed.timestamp > this.config.cacheTTL) {
                sessionStorage.removeItem(cacheKey);
                return null;
            }

            return parsed.data;
        } catch {
            return null;
        }
    }

    /**
     * Save settings to cache
     */
    private cacheSettings(imageUrls: string[], settings: PortalSettingsResponse): void {
        if (typeof sessionStorage === 'undefined') {
            return;
        }

        try {
            const cacheKey = this.getCacheKey(imageUrls);
            const cached: CachedSettings = {
                data: settings,
                timestamp: Date.now(),
            };
            sessionStorage.setItem(cacheKey, JSON.stringify(cached));

            if (this.config.debug) {
                console.log('[SettingsClient] Cached settings for', imageUrls.length, 'images');
            }
        } catch {
            // Ignore storage errors (quota exceeded, etc.)
            if (this.config.debug) {
                console.warn('[SettingsClient] Failed to cache settings');
            }
        }
    }

    /**
     * Generate cache key from image URLs
     */
    private getCacheKey(imageUrls: string[]): string {
        // Sort URLs for consistent cache key regardless of order
        const sortedUrls = [...imageUrls].sort();
        const hash = this.hashCode(sortedUrls.join('|'));
        return `${CACHE_KEY_PREFIX}${hash}`;
    }

    /**
     * Simple hash function for cache keys
     * (Fallback for environments without crypto.subtle)
     */
    private hashCode(str: string): string {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = (hash << 5) - hash + char;
            hash = hash & hash; // Convert to 32-bit integer
        }
        return Math.abs(hash).toString(16);
    }

    /**
     * Fetch settings from API with timeout handling
     *
     * Makes a POST request to the settings API endpoint with the list of image URLs.
     * Handles HTTP errors (401, 429, 500, etc.) and returns null on error for graceful fallback.
     */
    private async fetchWithTimeout(imageUrls: string[]): Promise<PortalSettingsResponse | null> {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

        try {
            const response = await fetch(this.config.endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    apiKey: this.config.apiKey,
                    images: imageUrls,
                }),
                signal: controller.signal,
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                if (response.status === 429) {
                    console.warn('[SettingsClient] Rate limit exceeded');
                } else if (response.status === 401) {
                    console.warn('[SettingsClient] Invalid API key');
                } else if (this.config.debug) {
                    console.warn(`[SettingsClient] API error: ${response.status} ${response.statusText}`);
                }
                return null;
            }

            return (await response.json()) as PortalSettingsResponse;

        } catch (error) {
            clearTimeout(timeoutId);

            if (error instanceof Error && error.name === 'AbortError') {
                if (this.config.debug) {
                    console.warn(`[SettingsClient] Request timed out after ${this.config.timeout}ms`);
                }
            } else if (this.config.debug) {
                console.warn('[SettingsClient] Network error:', error);
            }

            return null;
        }
    }
}
